/* === CUSTOM: product-description-expandable === */
/*
 * <custom-expandable-description> — height-based truncation with smooth
 * expand/collapse animation. Used by product description block in
 * snippets/product-info.liquid when block.settings.enable_truncation is true.
 *
 * State machine via data-attributes:
 *   data-truncated="initial" — pre-JS state, CSS clips, button hidden
 *   data-truncated="true"    — JS confirmed overflow, button visible
 *   data-truncated="false"   — content fits, no clipping, button hidden
 *   data-expanded="true|false" — drives fade opacity + button label/aria
 */

class CustomExpandableDescription extends HTMLElement {
  constructor() {
    super();
    this.handleToggle = this.handleToggle.bind(this);
    this.evaluateOverflow = this.evaluateOverflow.bind(this);
    this.handleExpandEnd = this.handleExpandEnd.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-custom-expandable-viewport]');
    this.content = this.querySelector('[data-custom-expandable-content]');
    this.toggle = this.querySelector('[data-custom-expandable-toggle]');
    this.labelMore = this.querySelector('[data-custom-expandable-label-more]');
    this.labelLess = this.querySelector('[data-custom-expandable-label-less]');

    if (!this.viewport || !this.content || !this.toggle) {
      return;
    }

    this.toggle.addEventListener('click', this.handleToggle);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.evaluateOverflow);
      this.resizeObserver.observe(this.content);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.evaluateOverflow);
    }
    this.evaluateOverflow();
  }

  disconnectedCallback() {
    if (this.toggle) {
      this.toggle.removeEventListener('click', this.handleToggle);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.viewport) {
      this.viewport.removeEventListener('transitionend', this.handleExpandEnd);
    }
  }

  getActiveCollapsedHeight() {
    const isMobile = window.matchMedia('(max-width: 699px)').matches;
    if (isMobile && this.dataset.mobileCollapsedHeight) {
      return parseInt(this.dataset.mobileCollapsedHeight, 10) || 160;
    }
    return parseInt(this.dataset.collapsedHeight, 10) || 240;
  }

  evaluateOverflow() {
    if (!this.content || !this.viewport || !this.toggle) return;
    if (this.dataset.expanded === 'true') return;

    const collapsedHeight = this.getActiveCollapsedHeight();
    const overflows = this.content.scrollHeight > collapsedHeight + 4;

    if (overflows) {
      this.dataset.truncated = 'true';
      this.toggle.hidden = false;
    } else {
      this.dataset.truncated = 'false';
      this.toggle.hidden = true;
      this.viewport.style.maxHeight = '';
    }
  }

  handleToggle() {
    if (this.dataset.expanded === 'true') {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    if (!this.viewport || !this.content) return;

    const targetHeight = this.content.scrollHeight;
    this.viewport.style.maxHeight = targetHeight + 'px';
    this.dataset.expanded = 'true';
    this.toggle.setAttribute('aria-expanded', 'true');
    this.labelMore.hidden = true;
    this.labelLess.hidden = false;

    this.viewport.addEventListener('transitionend', this.handleExpandEnd);
  }

  handleExpandEnd(event) {
    if (event.propertyName !== 'max-height') return;
    if (this.dataset.expanded !== 'true') return;
    this.viewport.style.maxHeight = 'none';
    this.viewport.removeEventListener('transitionend', this.handleExpandEnd);
  }

  collapse() {
    if (!this.viewport || !this.content) return;

    const currentHeight = this.content.scrollHeight;
    this.viewport.style.maxHeight = currentHeight + 'px';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.viewport.style.maxHeight = '';
        this.dataset.expanded = 'false';
        this.toggle.setAttribute('aria-expanded', 'false');
        this.labelMore.hidden = false;
        this.labelLess.hidden = true;
      });
    });
  }
}

if (!customElements.get('custom-expandable-description')) {
  customElements.define('custom-expandable-description', CustomExpandableDescription);
}
/* === END CUSTOM: product-description-expandable === */
